import React, { useRef, useCallback } from 'react';
import { Spinner } from 'reactstrap';
import { Activity } from '../../../Model/Activity/Activity';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import {
  SearchSection,
  SearchContainer,
  SearchIcon,
  SearchInput,
  ScrollableContent,
  LoadingContainer,
  ActivityList,
  ActivityItem,
  ActivityImage,
  ActivityInfo,
  ActivityName,
  ActivityCategory,
  NoResults,
  LoadMoreTrigger,
} from './LogActivityModal.styles';

// Extracted component to avoid hooks-in-loops
const ActivityListItem: React.FC<{
  activity: Activity;
  index: number;
  onSelect: (activity: Activity) => void;
}> = ({ activity, index, onSelect }) => {
  const activityName = useLocalized(activity.name);

  return (
    <ActivityItem onClick={() => onSelect(activity)} $delay={Math.min(index, 10)}>
      <ActivityImage>
        {activity.firstSmallestMediaUrl && <img src={activity.firstSmallestMediaUrl} alt={activityName} />}
      </ActivityImage>
      <ActivityInfo>
        <ActivityName>{activityName}</ActivityName>
        <ActivityCategory>{activity.tags[0]?.replace(/_/g, ' ') || 'Movement'}</ActivityCategory>
      </ActivityInfo>
    </ActivityItem>
  );
};

export type ActivitySelectStepProps = {
  activities: Activity[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  query: string;
  language: string;
  onQueryChange: (query: string) => void;
  onSelectActivity: (activity: Activity) => void;
  onLoadMore: () => void;
};

export const ActivitySelectStep: React.FC<ActivitySelectStepProps> = ({
  activities,
  loading,
  loadingMore,
  hasMore,
  query,
  language,
  onQueryChange,
  onSelectActivity,
  onLoadMore,
}) => {
  const { t } = useTrans();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loading || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      onLoadMore();
    }
  }, [loading, loadingMore, hasMore, onLoadMore]);

  return (
    <>
      <SearchSection>
        <SearchContainer>
          <SearchIcon>
            <i className="bi bi-search" />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder={t('activity.search.placeholder', { defaultValue: 'Search activities...' })}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            autoFocus
          />
        </SearchContainer>
      </SearchSection>

      <ScrollableContent ref={scrollContainerRef} onScroll={handleScroll}>
        {loading ? (
          <LoadingContainer>
            <Spinner color="primary" />
          </LoadingContainer>
        ) : (
          <>
            <ActivityList>
              {activities.map((activity, index) => (
                <ActivityListItem
                  key={activity.id}
                  activity={activity}
                  index={index}
                  onSelect={onSelectActivity}
                />
              ))}
              {activities.length === 0 && (
                <NoResults>{t('activity.search.noResults', { defaultValue: 'No activities found' })}</NoResults>
              )}
            </ActivityList>
            {(loadingMore || hasMore) && activities.length > 0 && (
              <LoadMoreTrigger>{loadingMore && <Spinner size="sm" color="primary" />}</LoadMoreTrigger>
            )}
          </>
        )}
      </ScrollableContent>
    </>
  );
};
